import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CallingTreeBackendService } from 'src/app/services/calling-tree-backend.service';
import { CallingTreeAdmins } from '../models/calling-tree-admins.model';
import { CallingTreeMember } from '../models/calling-tree-member.model';
import { CallingTreeSuperCoordinator } from '../models/calling-tree-super-coordinator.model';
import { CallingTreeView } from '../models/calling-tree-view.model';
import { CallingTree } from '../models/calling-tree.model';
import { UserDropDown } from '../models/user-drop-down.model';
import { MathUtility } from '../utility/math.utility';
import { Logout } from './auth.action';
import {
	AddUserToBranch,
	GetAvailableBranchMembers,
	GetCallingTreeAdmins,
	GetCallingTreeView,
	GetNumberOfBranches,
	RemoveUserFromBranch,
	SelectCallingTreeBranch,
	UpdateCallingTreeAdmins,
	UpdateCoordinators,
} from './calling-tree.action';
import { CallingTreeStateModel } from './calling-tree.model';
import { StartLoading, StopLoading } from './main.action';

export const DEFAULT_STATE: CallingTreeStateModel = {
	numberOfBranches: null,
	selectedCallingTree: null,
	selectedCallingTreeMembers: [],
	adminUsers: [],
	rootUserId: null,
	managerUserId: null,
	superCoordinators: [],
	callingTreeView: null,
	availableBranchMembers: null,
};

@State<CallingTreeStateModel>({
	name: 'callingTree',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class CallingTreeState {
	private callingTreeBackend = inject(CallingTreeBackendService);

	@Selector()
	static callingTreeState(
		state: CallingTreeStateModel
	): CallingTreeStateModel {
		return state;
	}

	@Selector()
	static numberOfBranches(state: CallingTreeStateModel): number {
		return state.numberOfBranches;
	}

	@Selector()
	static branchNumbers(state: CallingTreeStateModel): number[] {
		return state.numberOfBranches
			? MathUtility.arrayOfNumbers(state.numberOfBranches, false)
			: [];
	}

	@Selector()
	static callingTreeBranches(state: CallingTreeStateModel): number[] {
		return state.numberOfBranches
			? MathUtility.arrayOfNumbers(state.numberOfBranches)
			: [];
	}

	@Selector()
	static selectedCallingTree(state: CallingTreeStateModel): CallingTree {
		return state.selectedCallingTree;
	}

	@Selector()
	static selectedCallingTreeMembers(
		state: CallingTreeStateModel
	): CallingTreeMember[] {
		return state.selectedCallingTreeMembers;
	}

	@Selector()
	static adminUsers(state: CallingTreeStateModel): UserDropDown[] {
		return state.adminUsers;
	}

	@Selector()
	static rootUserId(state: CallingTreeStateModel): number {
		return state.rootUserId;
	}

	@Selector()
	static managerUserId(state: CallingTreeStateModel): number {
		return state.managerUserId;
	}

	@Selector()
	static superCoordinators(
		state: CallingTreeStateModel
	): CallingTreeSuperCoordinator[] {
		return state.superCoordinators;
	}

	@Selector()
	static admins(state: CallingTreeStateModel): CallingTreeAdmins {
		return Object.assign(Object.create(CallingTreeAdmins.prototype), {
			rootUserId: state.rootUserId,
			managerUserId: state.managerUserId,
			superCoordinators: state.superCoordinators,
		});
	}

	@Selector()
	static callingTreeView(state: CallingTreeStateModel): CallingTreeView {
		return state.callingTreeView;
	}

	@Selector()
	static availableBranchMembers(
		state: CallingTreeStateModel
	): UserDropDown[] {
		return state.availableBranchMembers;
	}

	@Action(GetNumberOfBranches)
	getNumberOfBranches(
		ctx: StateContext<CallingTreeStateModel>,
		_action: GetNumberOfBranches
	) {
		return this.callingTreeBackend
			.numberOfBranches()
			.pipe(
				tap((numberOfBranches) =>
					ctx.patchState({ numberOfBranches: numberOfBranches })
				)
			);
	}

	@Action(SelectCallingTreeBranch)
	selectCallingTreeBranch(
		ctx: StateContext<CallingTreeStateModel>,
		action: SelectCallingTreeBranch
	) {
		ctx.dispatch([new StartLoading()]);
		return this.callingTreeBackend
			.selectCallingTreeBranch(action.branch)
			.pipe(
				tap((callingTree) => {
					this.loadCallingTree(callingTree, ctx);
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	private loadCallingTree(
		callingTree: CallingTree,
		ctx: StateContext<CallingTreeStateModel>
	): void {
		ctx.patchState({
			selectedCallingTree: callingTree,
			selectedCallingTreeMembers: [...callingTree.members].sort(
				CallingTreeMember.sort
			),
		});
	}

	@Action(UpdateCoordinators)
	updateCoordinators(
		ctx: StateContext<CallingTreeStateModel>,
		action: UpdateCoordinators
	) {
		ctx.dispatch([new StartLoading()]);
		return this.callingTreeBackend
			.updateCoordinators(action.callingTree)
			.pipe(
				tap((callingTree) => {
					this.loadCallingTree(callingTree, ctx);
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	@Action(GetCallingTreeAdmins)
	getCallingTreeAdmins(
		ctx: StateContext<CallingTreeStateModel>,
		_action: GetCallingTreeAdmins
	) {
		ctx.dispatch([new StartLoading()]);
		return this.callingTreeBackend.getAdmins().pipe(
			tap((response) => {
				ctx.patchState({
					adminUsers: response.users.map((user) =>
						UserDropDown.fromJSON(user)
					),
					rootUserId: response.root,
					managerUserId: response.manager,
					superCoordinators: response.superCoordinators.map((sc) =>
						CallingTreeSuperCoordinator.fromJSON(sc)
					),
				});
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(UpdateCallingTreeAdmins)
	updateCallingTreeAdmins(
		ctx: StateContext<CallingTreeStateModel>,
		action: UpdateCallingTreeAdmins
	) {
		ctx.dispatch([new StartLoading()]);

		return this.callingTreeBackend.updateAdmins(action.admins).pipe(
			tap((response) => {
				ctx.patchState({
					rootUserId: response.root,
					managerUserId: response.manager,
					superCoordinators: response.superCoordinators.map((sc) =>
						CallingTreeSuperCoordinator.fromJSON(sc)
					),
				});
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(GetCallingTreeView)
	getCallingTreeView(
		ctx: StateContext<CallingTreeStateModel>,
		_action: GetCallingTreeView
	) {
		ctx.dispatch([new StartLoading()]);
		return this.callingTreeBackend.getView().pipe(
			tap((view) => {
				ctx.patchState({
					callingTreeView: view,
				});
				ctx.dispatch([new StopLoading()]);
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(GetAvailableBranchMembers)
	getAvailableBranchMembers(
		ctx: StateContext<CallingTreeStateModel>,
		action: GetAvailableBranchMembers
	) {
		ctx.patchState({ availableBranchMembers: null });
		ctx.dispatch([new StartLoading()]);
		return this.callingTreeBackend
			.getAvailableBranchMembers(action.branch)
			.pipe(
				tap((availableBranchMembers) => {
					ctx.patchState({
						availableBranchMembers: availableBranchMembers,
					});
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	@Action(AddUserToBranch)
	addUserToBranch(
		ctx: StateContext<CallingTreeStateModel>,
		action: AddUserToBranch
	) {
		ctx.dispatch([new StartLoading()]);
		return this.callingTreeBackend
			.addUserToBranch(action.userId, action.branch)
			.pipe(
				tap((members) => {
					ctx.patchState({
						selectedCallingTreeMembers: [...members].sort(
							CallingTreeMember.sort
						),
					});
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	@Action(RemoveUserFromBranch)
	removeUserFromBranch(
		ctx: StateContext<CallingTreeStateModel>,
		action: RemoveUserFromBranch
	) {
		ctx.dispatch([new StartLoading()]);
		return this.callingTreeBackend
			.removeUserFromBranch(action.userId, action.newBranch)
			.pipe(
				tap((members) => {
					ctx.patchState({
						selectedCallingTreeMembers: [...members].sort(
							CallingTreeMember.sort
						),
					});
					ctx.dispatch([new StopLoading()]);
				}),
				catchError((error) => {
					ctx.dispatch([new StopLoading()]);
					return throwError(() => error);
				})
			);
	}

	@Action(Logout)
	logout(ctx: StateContext<CallingTreeStateModel>, _action: Logout) {
		ctx.patchState(DEFAULT_STATE);
	}
}
